import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardMedia,
  Button,
  TextField,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Collapse,
  Stack,
  Rating,
  LinearProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  ShoppingCart as CartIcon,
  CloudUpload as UploadIcon
} from '@mui/icons-material';
import { productService, cartService, adminService, uploadService as apiUploadService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import uploadService from '../services/uploadService';
import { COLORS, withOpacity } from '../theme/colors';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, loading: authLoading } = useAuth();
  const { addToCart, loadCart } = useCart();
  const [product, setProduct] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [customText, setCustomText] = useState('');
  const [uploadedImages, setUploadedImages] = useState([]);
  const [addingToCart, setAddingToCart] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [wishlist, setWishlist] = useState([]);
  const [wishlistLoading, setWishlistLoading] = useState(false);
  const [expanded, setExpanded] = useState({ info: true, returns:false, shipping:false });
  const [similar, setSimilar] = useState([]);
  const [similarLoading, setSimilarLoading] = useState(false);

  // Reviews state
  const [reviewSummary, setReviewSummary] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsError, setReviewsError] = useState(null);
  const [reviewCursor, setReviewCursor] = useState(null);
  const [hasMoreReviews, setHasMoreReviews] = useState(false);
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [userHasReview, setUserHasReview] = useState(false);
  const [reviewForm, setReviewForm] = useState({ rating: 0, title: '', body: '' });
  const [reviewImages, setReviewImages] = useState([]); // {file, preview, name}
  const [reviewImageUploading, setReviewImageUploading] = useState(false);

  // --- Zoom & Pan State ---
  const [zoomed, setZoomed] = useState(false);
  const [zoomOrigin, setZoomOrigin] = useState({ x: '50%', y: '50%' });

  // Helper function to get category name from ID
  const getCategoryName = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : categoryId;
  };

  // Helper function to get category names for multiple categories
  const getProductCategoryNames = (product) => {
    if (product.categoryNames && Array.isArray(product.categoryNames)) {
      return product.categoryNames;
    } else if (product.categoryName) {
      return [product.categoryName];
    } else if (product.categories && Array.isArray(product.categories)) {
      return product.categories.map(catId => getCategoryName(catId));
    } else if (product.category) {
      return [getCategoryName(product.category)];
    }
    return [];
  };

  useEffect(() => {
    if (id) {
      fetchProduct();
    }
  }, [id]);

  useEffect(()=>{ if(currentUser && product){ loadWishlist(); } }, [currentUser, product]);
  useEffect(()=>{ if(product){ loadSimilar(); } }, [product]);
  useEffect(()=>{ if(product){ loadReviewSummary(); loadInitialReviews(); } }, [product]);
  useEffect(()=>{ if(currentUser && reviews.length){
    const exists = reviews.some(r=> r.userId === currentUser.uid);
    if(exists) setUserHasReview(true);
  } }, [currentUser, reviews]);

  useEffect(() => {
    // Only attempt to load admin categories if user is admin
    if (currentUser?.claims?.role === 'admin' || currentUser?.role === 'admin') {
      loadCategories();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const loadCategories = async () => {
    try {
      const response = await adminService.getCategories();
      if (response.data && Array.isArray(response.data)) {
        setCategories(response.data);
      }
    } catch (error) {
      // Suppress 403 for non-admins; only log other errors
      if (error?.response?.status !== 403) {
        console.error('Error loading categories:', error);
      }
    }
  };

  // Handle pending actions after login
  useEffect(() => {
    if (location.state?.executePendingAction && location.state?.pendingAction && currentUser && product) {
      const pendingAction = location.state.pendingAction;
      console.log('ProductDetail: Executing pending action after login:', pendingAction);
      
      if (pendingAction.type === 'ADD_TO_CART') {
        // Restore the form state
        setQuantity(pendingAction.quantity || 1);
        setCustomText(pendingAction.customization?.text || '');
        setUploadedImages(pendingAction.customization?.images || []);
        
        // Execute the add to cart action
        setTimeout(() => {
          executeAddToCart();
        }, 500); // Small delay to ensure state is set
      }
      
      // Clear the state to prevent re-execution
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, currentUser, product]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await productService.getProduct(id);
      setProduct(response.data);
      setError(null);
    } catch (error) {
      console.error('Error fetching product:', error);
      setError('Product not found');
    } finally {
      setLoading(false);
    }
  };

  const loadWishlist = async () => {
    try {
      setWishlistLoading(true);
      const res = await productService.getMyWishlist();
      setWishlist(res.data.wishlist||[]);
    } catch { setWishlist([]); } finally { setWishlistLoading(false); }
  };

  const toggleWishlist = async () => {
    if(!currentUser){
      sessionStorage.setItem('redirectAfterLogin', window.location.pathname);
      navigate('/login');
      return;
    }
    try {
      const inList = wishlist.includes(product.docId);
      if(inList){ await productService.removeFromWishlist(product.docId); }
      else { await productService.addToWishlist(product.docId); }
      // Server now also syncs a central wishlists collection (productId_userId) for price drop emails.
      await loadWishlist();
    } catch(e){ console.error('Wishlist toggle error', e); }
  };

  const loadSimilar = async () => {
    try {
      setSimilarLoading(true);
      const res = await productService.getSimilarProducts(product.docId, { limit:6 });
      setSimilar(res.data.products||[]);
    } catch(e){ setSimilar([]); } finally { setSimilarLoading(false); }
  };

  // ===== Reviews Logic =====
  const loadReviewSummary = async () => {
    try {
      const res = await productService.getProductReviewSummary(product.docId);
      setReviewSummary(res.data || null);
    } catch(e){
      console.error('Review summary load error', e);
      setReviewSummary(null);
    }
  };

  const loadInitialReviews = async () => {
    setReviews([]);
    setReviewCursor(null);
    await loadMoreReviews(true);
  };

  const loadMoreReviews = async (initial=false) => {
    if(reviewsLoading) return;
    try {
      setReviewsLoading(true);
      const params = { limit: 5 };
      if(reviewCursor) params.cursor = reviewCursor;
      const res = await productService.getProductReviews(product.docId, params);
      const newReviews = res.data.reviews || [];
      setReviews(prev => initial ? newReviews : [...prev, ...newReviews]);
      setReviewCursor(res.data.nextCursor || null);
      setHasMoreReviews(!!res.data.nextCursor);
      if(currentUser){
        const exists = (initial? newReviews: [...reviews, ...newReviews]).some(r=> r.userId === currentUser.uid);
        if(exists) setUserHasReview(true);
      }
    } catch(e){
      console.error('Reviews load error', e);
      setReviewsError('Failed to load reviews');
    } finally { setReviewsLoading(false); }
  };

  const handleReviewImageSelect = (e) => {
    const files = Array.from(e.target.files||[]).slice(0,6); // max 6 images
    const mapped = files.map(f=>({ file:f, preview:URL.createObjectURL(f), name:f.name }));
    setReviewImages(prev=>[...prev, ...mapped].slice(0,6));
  };
  const removeReviewImage = (index) => {
    setReviewImages(prev=>{
      const arr=[...prev];
      URL.revokeObjectURL(arr[index].preview);
      arr.splice(index,1);
      return arr;
    });
  };

  const submitReview = async () => {
    if(!currentUser){
      sessionStorage.setItem('redirectAfterLogin', window.location.pathname);
      navigate('/login');
      return;
    }
    if(userHasReview){
      alert('You have already submitted a review for this product.');
      return;
    }
    if(reviewForm.rating <=0){
      alert('Please select a rating.');
      return;
    }
    try {
      setReviewSubmitting(true);
      // Upload images first if any
      let imageUrls = [];
      if(reviewImages.length){
        setReviewImageUploading(true);
        const fd = new FormData();
        reviewImages.forEach(img=> fd.append('images', img.file));
        try {
          const res = await apiUploadService.uploadReviewImages(product.docId, fd);
          imageUrls = res.data.urls || [];
        } catch(e){
          console.error('Review image upload failed', e);
          alert('Image upload failed. You can try again or submit without images.');
          return;
        } finally { setReviewImageUploading(false); }
      }

      await productService.createProductReview(product.docId, {
        rating: reviewForm.rating,
        title: reviewForm.title?.trim() || null,
        body: reviewForm.body?.trim() || null,
        images: imageUrls
      });
      // Refresh summary & reviews
      await loadReviewSummary();
      await loadInitialReviews();
      setUserHasReview(true);
      setReviewForm({ rating:0, title:'', body:'' });
      setReviewImages([]);
      alert('Review submitted!');
    } catch(e){
      console.error('Review submit error', e);
      if(e.response?.status === 409){
        alert('You have already reviewed this product.');
        setUserHasReview(true);
      } else {
        alert('Failed to submit review. Please try again.');
      }
    } finally { setReviewSubmitting(false); }
  };

  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files);
    const maxImages = product.imageRequired || 0;
    
    if (files.length + uploadedImages.length > maxImages) {
      alert(`You can only upload ${maxImages} image(s) for this product.`);
      return;
    }

    const newImages = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name
    }));

    setUploadedImages(prev => [...prev, ...newImages]);
  };

  const removeUploadedImage = (index) => {
    setUploadedImages(prev => {
      const newImages = [...prev];
      URL.revokeObjectURL(newImages[index].preview);
      newImages.splice(index, 1);
      return newImages;
    });
  };

  const handleAddToCart = async () => {
    console.log('ProductDetail: handleAddToCart called, authLoading:', authLoading, 'currentUser:', currentUser);
    
    // Wait for auth loading to complete
    if (authLoading) {
      console.log('ProductDetail: Auth still loading, please wait');
      return;
    }
    
    if (!currentUser) {
      console.log('ProductDetail: User not authenticated, storing redirect and going to login');
      // Store the current page AND the action to redirect back after login
      sessionStorage.setItem('redirectAfterLogin', window.location.pathname);
      sessionStorage.setItem('pendingAction', JSON.stringify({
        type: 'ADD_TO_CART',
        productId: product.docId,
        quantity: quantity,
        customization: {
          text: customText || null,
          images: uploadedImages
        }
      }));
      navigate('/login');
      return;
    }

    // Validate customization requirements
    if (product.imageRequired > 0 && uploadedImages.length !== product.imageRequired) {
      alert(`Please upload exactly ${product.imageRequired} image(s) for this product.`);
      return;
    }

    if (product.textRequired && !customText.trim()) {
      alert('Please enter the required custom text.');
      return;
    }

    await executeAddToCart();
  };

  const executeAddToCart = async () => {
    try {
      setAddingToCart(true);
      
      // Upload customization images first if they exist
      let imageUrls = [];
      if (uploadedImages.length > 0) {
        console.log('ProductDetail: Uploading customization images...');
        setUploadingImages(true);
        try {
          const uploadResults = await uploadService.uploadCartCustomizationImages(
            uploadedImages.map(img => img.file),
            currentUser.uid,
            product.docId,
            (progress, index) => {
              console.log(`Upload progress for image ${index + 1}: ${progress}%`);
            }
          );
          imageUrls = uploadResults.map(result => result.url);
          console.log('ProductDetail: Images uploaded successfully:', imageUrls);
        } catch (uploadError) {
          console.error('Error uploading images:', uploadError);
          alert('Failed to upload images. Please try again.');
          return;
        } finally {
          setUploadingImages(false);
        }
      }
      
      const cartItem = {
        productId: product.docId,
        quantity: quantity,
        customizations: {
          text: customText || null,
          images: imageUrls // Now using uploaded image URLs
        }
      };

      console.log('ProductDetail: Adding to cart:', cartItem);
      await cartService.addToCart(cartItem);
      
      // Refresh the cart context to update the cart icon count
      await loadCart();
      
      alert('Product added to cart!');
      // Redirect to cart page
      navigate('/cart');
    } catch (error) {
      console.error('Error adding to cart:', error);
      if (error.response?.status === 401) {
        console.log('ProductDetail: 401 error, token might be expired');
        // Token expired, redirect to login
        sessionStorage.setItem('redirectAfterLogin', window.location.pathname);
        sessionStorage.setItem('pendingAction', JSON.stringify({
          type: 'ADD_TO_CART',
          productId: product.docId,
          quantity: quantity,
          customization: {
            text: customText || null,
            images: uploadedImages
          }
        }));
        navigate('/login');
      } else {
        alert('Failed to add product to cart. Please try again.');
      }
    } finally {
      setAddingToCart(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={() => navigate('/')}>
          Back to Home
        </Button>
      </Container>
    );
  }

  if (!product) return null;

  const hasValidSale = product.salePrice && Number(product.salePrice) > 0 && Number(product.salePrice) < Number(product.price || 0);
  const unitPrice = hasValidSale ? Number(product.salePrice) : Number(product.price);
  const totalPrice = unitPrice * quantity;
  const inWishlist = wishlist.includes(product.docId);

  const ZOOM_SCALE = 1.6;

  const handleImageMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setZoomOrigin({ x: `${x}%`, y: `${y}%` });
  };
  const handleImageMouseEnter = () => setZoomed(true);
  const handleImageMouseLeave = () => setZoomed(false);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ border:`1px solid ${withOpacity(COLORS.primary.main,0.15)}`, borderRadius:3, p:1, bgcolor:COLORS.background.paper }}>
            <Box sx={{ position:'relative', borderRadius:2, overflow:'hidden', bgcolor:withOpacity(COLORS.primary.main,0.04) }}>
              <Box
                sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  borderRadius: 2,
                  cursor: zoomed ? 'zoom-out' : 'zoom-in',
                  height: 450,
                  background: COLORS.background.paper
                }}
                onMouseMove={handleImageMouseMove}
                onMouseEnter={handleImageMouseEnter}
                onMouseLeave={handleImageMouseLeave}
              >
                <CardMedia
                  component="img"
                  image={product.imageUrls[selectedImage] || '/placeholder-image.jpg'}
                  alt={product.name}
                  sx={{
                    objectFit: 'cover',
                    maxHeight: 450,
                    width: '100%',
                    height: '100%',
                    transition: 'transform 0.4s cubic-bezier(.4,2,.6,1)',
                    transform: zoomed ? `scale(${ZOOM_SCALE})` : 'scale(1)',
                    transformOrigin: `${zoomOrigin.x} ${zoomOrigin.y}`
                  }}
                />
              </Box>
              <IconButton onClick={toggleWishlist} disabled={wishlistLoading} sx={{ position:'absolute', top:12, right:12, bgcolor:COLORS.background.paper, '&:hover':{ bgcolor:withOpacity(COLORS.primary.main,0.15) } }}>
                {inWishlist ? <FavoriteIcon htmlColor={COLORS.primary.main}/> : <FavoriteBorderIcon />}
              </IconButton>
            </Box>
            {product.imageUrls && product.imageUrls.length > 1 && (
              <Box display="flex" gap={1} mt={2} flexWrap="wrap">
                {product.imageUrls.map((imageUrl, index) => (
                  <Box key={index} sx={{ width:70,height:70, borderRadius:2, overflow:'hidden', cursor:'pointer', border:selectedImage===index?`2px solid ${COLORS.primary.main}`:`1px solid ${COLORS.border?.light || '#e5e7eb'}` }} onClick={()=>setSelectedImage(index)}>
                    <CardMedia component="img" image={imageUrl} alt={`${product.name} ${index+1}`} sx={{ width:'100%', height:'100%', objectFit:'cover' }} />
                  </Box>
                ))}
              </Box>
            )}

            {/* YouTube Video */}
            {product.youtubeUrl && (
              <Box mt={3} sx={{ position:'relative', paddingTop:'56.25%' }}>
                <iframe
                  src={`https://www.youtube.com/embed/${(() => {
                    const match = product.youtubeUrl.match(/[?&]v=([^&#]+)/);
                    return match ? match[1] : product.youtubeUrl.split('/').pop();
                  })()}`}
                  title="Product Video"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{ position:'absolute', top:0, left:0, width:'100%', height:'100%', borderRadius:12 }}
                />
              </Box>
            )}
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box sx={{ pr:{md:3} }}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
              <Typography variant="h4" sx={{ fontWeight:700 }}>{product.name}</Typography>
            </Stack>
            <Box sx={{ mt:1, mb:2 }}>
              {getProductCategoryNames(product).map((categoryName,i)=>(<Chip key={i} label={categoryName} size="small" sx={{ mr:1, mb:1 }} />))}
            </Box>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              {hasValidSale ? (
                <>
                  <Typography variant="h4" sx={{ fontWeight:700, color:COLORS.primary.main }}>
                    ₹{unitPrice.toLocaleString()}
                  </Typography>
                  <Typography variant="body1" sx={{ color:COLORS.text.secondary, textDecoration:'line-through' }}>
                    ₹{Number(product.price).toLocaleString()}
                  </Typography>
                  <Chip color="error" size="small" label={`${Math.round(((Number(product.price)-unitPrice)/Number(product.price))*100)}% OFF`} />
                </>
              ) : (
                <Typography variant="h4" sx={{ fontWeight:700, color:COLORS.primary.main }}>
                  ₹{Number(product.price).toLocaleString()}
                </Typography>
              )}
            </Box>
            <Box
              component="div"
              sx={{
                fontSize: '1rem',
                lineHeight: 1.5,
                color: 'text.secondary',
                mb: 2,
                '& p': { margin: '0 0 8px 0' },
                '& ul': { margin: '0 0 8px 0', paddingLeft: '16px' },
                '& li': { marginBottom: '2px' },
                '& strong': { fontWeight: 600 },
                '& .ql-indent-1': { paddingLeft: '16px' },
                '& .ql-indent-2': { paddingLeft: '32px' },
                '& .ql-indent-3': { paddingLeft: '48px' }
              }}
              dangerouslySetInnerHTML={{ 
                __html: product.shortDescription || ''
              }}
            />

            {/* Template Type Info */}
            <Box sx={{ mb: 2 }}>
              <Chip 
                label={`Template: ${product.templateType || 'standard'}`} 
                size="small" 
                sx={{ mr: 1 }}
              />
              {product.imageRequired > 0 && (
                <Chip 
                  label={`Requires ${product.imageRequired} image(s)`} 
                  size="small" 
                  color="info"
                  sx={{ mr: 1 }}
                />
              )}
              {product.textRequired && (
                <Chip 
                  label="Custom text required" 
                  size="small" 
                  color="warning"
                />
              )}
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Customization Options */}
            {(product.textRequired || product.imageRequired > 0) && (
              <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Customization Options
                </Typography>

                {/* Text Input */}
                {product.textRequired && (
                  <TextField
                    fullWidth
                    label="Custom Text"
                    placeholder={product.customizationOptions?.textField?.placeholder || "Enter your custom text"}
                    value={customText}
                    onChange={(e) => setCustomText(e.target.value)}
                    multiline
                    rows={2}
                    sx={{ mb: 2 }}
                    inputProps={{
                      maxLength: product.customizationOptions?.textField?.maxLength || 50
                    }}
                    helperText={`${customText.length}/${product.customizationOptions?.textField?.maxLength || 50} characters`}
                  />
                )}

                {/* Image Upload */}
                {product.imageRequired > 0 && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Upload {product.imageRequired} Image(s):
                    </Typography>
                    
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="image-upload"
                      multiple={product.imageRequired > 1}
                      type="file"
                      onChange={handleImageUpload}
                    />
                    <label htmlFor="image-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<UploadIcon />}
                        disabled={uploadedImages.length >= product.imageRequired}
                        sx={{ mb: 1 }}
                      >
                        {uploadedImages.length >= product.imageRequired 
                          ? `${product.imageRequired} Image(s) Uploaded` 
                          : 'Upload Images'
                        }
                      </Button>
                    </label>

                    {/* Preview Uploaded Images */}
                    {uploadedImages.length > 0 && (
                      <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
                        {uploadedImages.map((img, index) => (
                          <Box key={index} position="relative">
                            <img
                              src={img.preview}
                              alt={img.name}
                              style={{
                                width: 60,
                                height: 60,
                                objectFit: 'cover',
                                borderRadius: 8,
                                border: '1px solid #ddd'
                              }}
                            />
                            <IconButton
                              size="small"
                              onClick={() => removeUploadedImage(index)}
                              sx={{
                                position: 'absolute',
                                top: -8,
                                right: -8,
                                bgcolor: 'error.main',
                                color: 'white',
                                '&:hover': { bgcolor: 'error.dark' }
                              }}
                            >
                              <RemoveIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                )}
              </Paper>
            )}

            {/* Quantity Selection */}
            <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
              <Typography variant="body1">Quantity:</Typography>
              <Box display="flex" alignItems="center">
                <IconButton 
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={quantity <= 1}
                >
                  <RemoveIcon />
                </IconButton>
                <Typography variant="h6" sx={{ mx: 2, minWidth: '2ch', textAlign: 'center' }}>
                  {quantity}
                </Typography>
                <IconButton onClick={() => setQuantity(quantity + 1)}>
                  <AddIcon />
                </IconButton>
              </Box>
            </Box>

            {/* Total Price (removed as per request) */}

            {/* Add to Cart Button */}
            <Stack direction={{ xs:'column', sm:'row' }} spacing={2}>
              <Button
                variant="contained"
                size="large"
                startIcon={<CartIcon />}
                onClick={handleAddToCart}
                disabled={authLoading || addingToCart || uploadingImages || !product.inStock}
                sx={{ flex:1, py:1.4 }}
              >
                {authLoading ? 'Loading...' : uploadingImages ? 'Uploading Images...' : addingToCart ? 'Adding...' : !product.inStock ? 'Out of Stock' : 'Add to Cart'}
              </Button>
              <Button
                variant={inWishlist? 'outlined':'contained'}
                color={inWishlist? 'secondary':'primary'}
                startIcon={inWishlist?<FavoriteIcon/>:<FavoriteBorderIcon/>}
                onClick={toggleWishlist}
                disabled={wishlistLoading}
                sx={{ minWidth:170 }}
              >
                {inWishlist? 'Wishlisted':'Add to Wishlist'}
              </Button>
            </Stack>

            {!product.inStock && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                This product is currently out of stock.
              </Alert>
            )}
          </Box>
        </Grid>
      </Grid>
      {/* Collapsible Sections */}
      <Box sx={{ mt:6 }}>
        {/* Product Info */}
        <Box sx={{ border:`1px solid ${withOpacity(COLORS.primary.main,0.15)}`, borderRadius:2, mb:2 }}>
          <Box onClick={()=>setExpanded(p=>({...p, info:!p.info}))} sx={{ cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'space-between', px:2, py:1.5, bgcolor:withOpacity(COLORS.primary.main,0.05) }}>
            <Typography variant="subtitle1" sx={{ fontWeight:600 }}>Product Info</Typography>
            {expanded.info ? <ExpandLessIcon/> : <ExpandMoreIcon/>}
          </Box>
          <Collapse in={expanded.info} timeout="auto" unmountOnExit>
            <Box sx={{ px:3, py:2 }}>
              <Box
                component="div"
                sx={{
                  fontSize: '0.875rem',
                  lineHeight: 1.43,
                  color: 'text.primary',
                  '& p': { margin: '0 0 16px 0' },
                  '& ul': { margin: '0 0 16px 0', paddingLeft: '20px' },
                  '& li': { marginBottom: '4px' },
                  '& strong': { fontWeight: 600 },
                  '& .ql-indent-1': { paddingLeft: '20px' },
                  '& .ql-indent-2': { paddingLeft: '40px' },
                  '& .ql-indent-3': { paddingLeft: '60px' },
                  '& .ql-indent-4': { paddingLeft: '80px' },
                  '& .ql-indent-5': { paddingLeft: '100px' },
                  '& .ql-indent-6': { paddingLeft: '120px' },
                  '& .ql-indent-7': { paddingLeft: '140px' },
                  '& .ql-indent-8': { paddingLeft: '160px' }
                }}
                dangerouslySetInnerHTML={{ 
                  __html: product.longDescription || product.shortDescription || ''
                }}
              />
            </Box>
          </Collapse>
        </Box>
        {/* Return & Refund */}
        <Box sx={{ border:`1px solid ${withOpacity(COLORS.primary.main,0.1)}`, borderRadius:2, mb:2 }}>
          <Box onClick={()=>setExpanded(p=>({...p, returns:!p.returns}))} sx={{ cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'space-between', px:2, py:1.5, bgcolor:withOpacity(COLORS.primary.main,0.04) }}>
            <Typography variant="subtitle1" sx={{ fontWeight:600 }}>Return & Refund Policy</Typography>
            {expanded.returns ? <ExpandLessIcon/> : <ExpandMoreIcon/>}
          </Box>
          <Collapse in={expanded.returns} unmountOnExit>
            <Box sx={{ px:3, py:2 }}>
              <Typography variant="body2" component="div">
                <ul style={{ marginTop:0 }}>
                  <li>7-day return window for manufacturing defects or transit damage.</li>
                  <li>Customized / personalized items are only eligible for replacement if defective.</li>
                  <li>Refunds processed to original payment method within 5-7 business days.</li>
                  <li>Contact support before sending any item back.</li>
                </ul>
              </Typography>
            </Box>
          </Collapse>
        </Box>
        {/* Shipping Info */}
        <Box sx={{ border:`1px solid ${withOpacity(COLORS.primary.main,0.1)}`, borderRadius:2, mb:6 }}>
          <Box onClick={()=>setExpanded(p=>({...p, shipping:!p.shipping}))} sx={{ cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'space-between', px:2, py:1.5, bgcolor:withOpacity(COLORS.primary.main,0.04) }}>
            <Typography variant="subtitle1" sx={{ fontWeight:600 }}>Shipping Info</Typography>
            {expanded.shipping ? <ExpandLessIcon/> : <ExpandMoreIcon/>}
          </Box>
          <Collapse in={expanded.shipping} unmountOnExit>
            <Box sx={{ px:3, py:2 }}>
              <Typography variant="body2" component="div">
                <ul style={{ marginTop:0 }}>
                  <li>Standard dispatch 2-4 business days. Personalized items may need extra time.</li>
                  <li>Tracking details emailed once shipped.</li>
                  <li>Free shipping threshold applied automatically where eligible.</li>
                </ul>
              </Typography>
            </Box>
          </Collapse>
        </Box>
      </Box>
      {/* Similar Products */}
      {/* Reviews Section */}
      <Box sx={{ mt:8 }}>
        <Typography variant="h5" sx={{ fontWeight:600, mb:3 }}>Customer Reviews</Typography>
        {/* Summary */}
        <Box sx={{ display:'flex', flexDirection:{ xs:'column', md:'row' }, gap:4, mb:4 }}>
          <Paper elevation={0} sx={{ flex:'0 0 260px', p:3, border:`1px solid ${withOpacity(COLORS.primary.main,0.15)}`, borderRadius:3 }}>
            <Typography variant="h3" sx={{ fontWeight:700, lineHeight:1 }}>{(reviewSummary?.averageRating || product.averageRating || 0).toFixed(1)}</Typography>
            <Rating value={Number(reviewSummary?.averageRating || product.averageRating || 0)} precision={0.1} readOnly sx={{ mt:0.5 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mt:1 }}>
              Based on {reviewSummary?.ratingCount || product.ratingCount || 0} review(s)
            </Typography>
            {/* Breakdown */}
            <Box sx={{ mt:2 }}>
              {[5,4,3,2,1].map(star=>{
                const count = reviewSummary?.ratingBreakdown?.[star] || 0;
                const total = reviewSummary?.ratingCount || 0;
                const pct = total? (count/total)*100 : 0;
                return (
                  <Box key={star} sx={{ display:'flex', alignItems:'center', mb:0.5 }}>
                    <Typography variant="caption" sx={{ width:24 }}>{star}★</Typography>
                    <Box sx={{ flex:1, mx:1 }}><LinearProgress variant="determinate" value={pct} sx={{ height:6, borderRadius:3 }} /></Box>
                    <Typography variant="caption" sx={{ minWidth:28, textAlign:'right' }}>{count}</Typography>
                  </Box>
                );
              })}
            </Box>
          </Paper>
          {/* Write Review */}
          <Paper elevation={0} sx={{ flex:1, p:3, border:`1px solid ${withOpacity(COLORS.primary.main,0.12)}`, borderRadius:3 }}>
            {userHasReview ? (
              <Alert severity="info">You've already reviewed this product.</Alert>
            ) : (
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight:600, mb:1 }}>Write a Review</Typography>
                <Rating value={reviewForm.rating} onChange={(_,v)=> setReviewForm(f=>({...f, rating:v}))} />
                <TextField
                  fullWidth
                  label="Title (optional)"
                  size="small"
                  sx={{ mt:2, mb:1 }}
                  value={reviewForm.title}
                  onChange={e=> setReviewForm(f=>({...f, title:e.target.value}))}
                  inputProps={{ maxLength:80 }}
                  helperText={`${reviewForm.title.length}/80`}
                />
                <TextField
                  fullWidth
                  label="Your review (optional)"
                  multiline
                  minRows={3}
                  value={reviewForm.body}
                  onChange={e=> setReviewForm(f=>({...f, body:e.target.value}))}
                  inputProps={{ maxLength:800 }}
                  helperText={`${reviewForm.body.length}/800`}
                />
                {/* Review Images */}
                <Box sx={{ mt:2 }}>
                  <input type="file" accept="image/*" multiple hidden id="review-image-input" onChange={handleReviewImageSelect} />
                  <label htmlFor="review-image-input">
                    <Button variant="outlined" size="small" component="span" disabled={reviewImages.length>=6 || reviewImageUploading}>
                      {reviewImageUploading ? 'Uploading...' : reviewImages.length>=6? 'Max Images' : 'Add Images'}
                    </Button>
                  </label>
                  {reviewImages.length>0 && (
                    <Box sx={{ display:'flex', gap:1, flexWrap:'wrap', mt:1 }}>
                      {reviewImages.map((img,i)=>(
                        <Box key={i} sx={{ position:'relative' }}>
                          <img src={img.preview} alt={img.name} style={{ width:70, height:70, objectFit:'cover', borderRadius:8, border:'1px solid #ddd' }} />
                          <IconButton size="small" onClick={()=>removeReviewImage(i)} sx={{ position:'absolute', top:-8, right:-8, bgcolor:'error.main', color:'white', '&:hover':{ bgcolor:'error.dark' } }}>
                            <RemoveIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
                <Button
                  variant="contained"
                  sx={{ mt:2 }}
                  disabled={reviewSubmitting || reviewImageUploading || !reviewForm.rating}
                  onClick={submitReview}
                >
                  {reviewSubmitting? 'Submitting...' : 'Submit Review'}
                </Button>
              </Box>
            )}
          </Paper>
        </Box>
        {/* Reviews List */}
        <Box>
          {reviewsLoading && !reviews.length && <CircularProgress size={32} />}
          {reviewsError && <Alert severity="error" sx={{ mb:2 }}>{reviewsError}</Alert>}
          {!reviewsLoading && !reviews.length && <Typography variant="body2" color="text.secondary">No reviews yet. Be the first to review!</Typography>}
          <Grid container spacing={2}>
            {reviews.map(r=> (
              <Grid item xs={12} key={r.id}>
                <Paper elevation={0} sx={{ p:2, border:`1px solid ${withOpacity(COLORS.primary.main,0.12)}`, borderRadius:3 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Rating value={r.rating} precision={0.5} readOnly size="small" />
                      <Typography variant="subtitle2" sx={{ fontWeight:600 }}>{r.title || `Rated ${r.rating}★`}</Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">{r.createdAt ? new Date(r.createdAt._seconds? r.createdAt._seconds*1000 : r.createdAt).toLocaleDateString() : ''}</Typography>
                  </Box>
                  {r.body && <Typography variant="body2" sx={{ mb:1, whiteSpace:'pre-line' }}>{r.body}</Typography>}
                  {r.images && r.images.length>0 && (
                    <Box sx={{ display:'flex', gap:1, flexWrap:'wrap', mb:1 }}>
                      {r.images.map((img,i)=>(
                        <Card key={i} sx={{ width:80, height:80, borderRadius:2, overflow:'hidden', cursor:'pointer' }}>
                          <CardMedia component="img" image={img} alt={`review ${i+1}`} sx={{ width:'100%', height:'100%', objectFit:'cover' }} />
                        </Card>
                      ))}
                    </Box>
                  )}
                  <Typography variant="caption" color="text.secondary">— {r.userName || 'Anonymous'}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
          {hasMoreReviews && (
            <Box sx={{ mt:3 }}>
              <Button variant="outlined" onClick={()=>loadMoreReviews()} disabled={reviewsLoading}>
                {reviewsLoading? 'Loading...' : 'Load More'}
              </Button>
            </Box>
          )}
        </Box>
      </Box>
      <Box sx={{ mt:4 }}>
        <Typography variant="h5" sx={{ fontWeight:600, mb:2 }}>You May Also Like</Typography>
        {similarLoading ? <CircularProgress size={32}/> : (
          <Grid container spacing={2}>
            {similar.map(sp=> (
              <Grid item xs={6} sm={4} md={2} key={sp.docId}>
                <Card onClick={()=>navigate(`/products/${sp.docId}`)} sx={{ cursor:'pointer', transition:'transform .2s', '&:hover':{ transform:'translateY(-4px)', boxShadow:3 } }}>
                  <CardMedia component="img" image={sp.imageUrls?.[0]||'/placeholder-image.jpg'} alt={sp.name} sx={{ height:140, objectFit:'cover' }} />
                  <Box sx={{ p:1 }}>
                    <Typography variant="body2" noWrap>{sp.name}</Typography>
                    <Typography variant="caption" color="text.secondary">₹{Number(sp.salePrice||sp.price||0).toLocaleString()}</Typography>
                  </Box>
                </Card>
              </Grid>
            ))}
            {!similar.length && !similarLoading && <Typography variant="body2" color="text.secondary" sx={{ ml:2 }}>No suggestions yet.</Typography>}
          </Grid>
        )}
      </Box>
    </Container>
  );
};

export default ProductDetail;
